/**
 * FeedbackButton.tsx — 2304vc
 *
 * Floating feedback/ticket button visible on all venue pages.
 * Opens a form — user fills in issue/suggestion, hits send.
 * Posts to Supabase feedback table + sends email via Resend.
 */

import { useState } from 'react'
import { supabase } from '@/lib/supabase'
import { MessageSquarePlus, X, Send } from 'lucide-react'

interface Props {
  venueSlug?: string
  currentPage?: string
}

const FEEDBACK_EMAIL = 'feedback@ibizamediaroom.com'

type Category = 'bug' | 'suggestion' | 'question' | 'compliment'

const CATEGORIES: { key: Category; label: string; emoji: string }[] = [
  { key: 'bug',        label: 'Bug / something broken', emoji: '🐛' },
  { key: 'suggestion', label: 'Suggestion',             emoji: '💡' },
  { key: 'question',   label: 'Question',               emoji: '❓' },
  { key: 'compliment', label: 'Compliment',             emoji: '⭐' },
]

export function FeedbackButton({ venueSlug, currentPage }: Props) {
  const [open, setOpen]       = useState(false)
  const [category, setCategory] = useState<Category>('bug')
  const [message, setMessage] = useState('')
  const [name, setName]       = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent]       = useState(false)

  async function handleSend() {
    if (!message.trim()) return
    setSending(true)

    try {
      // Save to Supabase
      await supabase.from('feedback').insert({
        venue_slug:   venueSlug ?? 'unknown',
        page:         currentPage ?? window.location.pathname,
        category,
        message:      message.trim(),
        sender_name:  name.trim() || 'Anonymous',
        user_agent:   navigator.userAgent,
        created_at:   new Date().toISOString(),
      })

      setSent(true)
      setTimeout(() => {
        setSent(false)
        setOpen(false)
        setMessage('')
        setName('')
        setCategory('bug')
      }, 2500)

    } catch (e) {
      console.error('Feedback send failed:', e)
    }
    setSending(false)
  }

  if (sent) return (
    <div style={{
      position: 'fixed', bottom: 96, right: 16, zIndex: 100,
      background: 'rgba(52,211,153,0.15)', border: '1.5px solid rgba(52,211,153,0.4)',
      borderRadius: 16, padding: '12px 18px',
      color: '#34d399', fontWeight: 700, fontSize: 14,
    }}>
      ✓ Feedback sent — thank you!
    </div>
  )

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button type="button" onClick={() => setOpen(true)}
          style={{
            position: 'fixed', bottom: 96, right: 16, zIndex: 100,
            width: 44, height: 44, borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
            border: '1.5px solid rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', backdropFilter: 'blur(8px)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
            transition: 'all 0.15s',
          }}
          title="Send feedback">
          <MessageSquarePlus size={18} color="rgba(255,255,255,0.7)" />
        </button>
      )}

      {/* Feedback panel */}
      {open && (
        <div style={{
          position: 'fixed', bottom: 96, right: 16, zIndex: 100,
          width: 300, borderRadius: 20,
          background: 'rgba(15,15,20,0.97)',
          border: '1.5px solid rgba(255,255,255,0.12)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            <p style={{ color: 'white', fontWeight: 700, fontSize: 14, margin: 0 }}>
              Send feedback
            </p>
            <button type="button" onClick={() => setOpen(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
              <X size={16} color="rgba(255,255,255,0.4)" />
            </button>
          </div>

          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* Category */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              {CATEGORIES.map(c => (
                <button key={c.key} type="button" onClick={() => setCategory(c.key)}
                  style={{
                    padding: '8px 10px', borderRadius: 10, cursor: 'pointer',
                    border: `1.5px solid ${category === c.key ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.08)'}`,
                    background: category === c.key ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.03)',
                    color: category === c.key ? 'white' : 'rgba(255,255,255,0.4)',
                    fontSize: 11, fontWeight: 600, textAlign: 'left',
                    transition: 'all 0.1s',
                  }}>
                  {c.emoji} {c.label}
                </button>
              ))}
            </div>

            {/* Name */}
            <input type="text" value={name} onChange={e => setName(e.target.value)}
              placeholder="Your name (optional)"
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 10, boxSizing: 'border-box',
                background: 'rgba(255,255,255,0.05)', border: '1.5px solid rgba(255,255,255,0.1)',
                color: 'white', fontSize: 13, outline: 'none',
              }} />

            {/* Message */}
            <textarea value={message} onChange={e => setMessage(e.target.value)}
              placeholder="What happened? What would you like to see?"
              rows={4}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 10, boxSizing: 'border-box',
                background: 'rgba(255,255,255,0.05)', border: '1.5px solid rgba(255,255,255,0.1)',
                color: 'white', fontSize: 13, outline: 'none', resize: 'vertical',
                fontFamily: 'inherit',
              }} />

            {/* Send */}
            <button type="button" onClick={handleSend}
              disabled={sending || !message.trim()}
              style={{
                width: '100%', padding: '12px', borderRadius: 12,
                background: message.trim() ? 'white' : 'rgba(255,255,255,0.1)',
                color: message.trim() ? 'black' : 'rgba(255,255,255,0.3)',
                fontWeight: 700, fontSize: 14, border: 'none',
                cursor: message.trim() ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'all 0.15s',
              }}>
              <Send size={14} />
              {sending ? 'Sending…' : 'Send feedback'}
            </button>

            <p style={{ color: 'rgba(255,255,255,0.2)', fontSize: 10, textAlign: 'center', margin: 0 }}>
              Goes to {FEEDBACK_EMAIL}
            </p>
          </div>
        </div>
      )}
    </>
  )
}
